import { FC, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Icon, Modal, LottieAnimation } from 'src/components';
import { defaultAnimationOptions } from 'src/components/constants';
import stageUpAnimation from '../assets/stage-up-animation.json';
import soundFile from '../assets/stage-up-celebration.mp3';
import * as S from './styles';

const msgs = defineMessages({
  nextStage: { id: 'home_celebration_stageup_title' },
  nextStageLevels: { id: 'eos_levelUp_celebration_stageReached_nextStageLevels_web' },
  nextStageInfiniteLevels: {
    id: 'eos_levelUp_celebration_stageReached_nextStageLevels_lastStage_web',
  },
  continue: { id: 'Continue' },
});

const LEVELS_PER_STAGE = 5;
const INFINITE_STAGE_START_LVL = 31;

export const StageUpCelebration: FC<{
  stageName: string;
  stageDescription: string;
  level?: number;
  onClickContinue: () => void;
  isOpen: boolean;
}> = ({ stageName, stageDescription, level, onClickContinue, isOpen }) => {
  const intl = useIntl();

  const badgeText = useMemo(() => {
    if (level === undefined) {
      return intl.formatMessage(msgs.nextStage);
    }

    const startLevel = Math.floor((level - 1) / LEVELS_PER_STAGE) * LEVELS_PER_STAGE + 1;
    const endLevel = startLevel + LEVELS_PER_STAGE - 1;

    if (level >= INFINITE_STAGE_START_LVL) {
      return intl.formatMessage(msgs.nextStageInfiniteLevels, { level: startLevel });
    }

    return intl.formatMessage(msgs.nextStageLevels, {
      levelA: startLevel,
      levelB: endLevel,
    });
  }, [intl, level]);

  return (
    <Modal isOpen={isOpen} fullSize hideCloseButton>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio autoPlay preload="auto" src={soundFile} data-testid="stage-up-sound" />

      <S.ModalContent>
        <S.Center>
          <S.AnimationRoot>
            <S.AnimationContainer data-testid="stage-up-animation">
              <LottieAnimation {...defaultAnimationOptions} animationData={stageUpAnimation} loop />
            </S.AnimationContainer>
          </S.AnimationRoot>
          <S.NextStage>
            <Icon name="flag" />
            {badgeText}
          </S.NextStage>
          <S.Header>{stageName}</S.Header>
          <S.Description>{stageDescription}</S.Description>
          <Button buttonSize="medium" buttonType="primary" onClick={onClickContinue}>
            {intl.formatMessage(msgs.continue)}
          </Button>
        </S.Center>
      </S.ModalContent>
    </Modal>
  );
};
